import React from 'react';

/**
 * ConfigErrorBoundary - Fail Fast Error Boundary for Configuration Issues
 * Catches configuration-related errors at React runtime and displays user-friendly error page
 */
class ConfigErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      isConfigError: false
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a configuration-related error
    const isConfigError = 
      error.message?.includes('config') ||
      error.message?.includes('CONFIG') ||
      error.message?.includes('configuration') ||
      error.message?.includes('Configuration');

    return { 
      hasError: true,
      isConfigError
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ConfigErrorBoundary caught an error:', error, errorInfo);
    
    // Log to monitoring service if available
    if (window.Sentry) {
      window.Sentry.captureException(error);
    }

    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Configuration error - show specific message
      if (this.state.isConfigError) {
        return (
          <div style={styles.container}>
            <div style={styles.errorCard}>
              <div style={styles.errorIcon}>⚠️</div>
              <h1 style={styles.title}>Configuration Error</h1>
              <p style={styles.message}>
                The application encountered a configuration issue and cannot continue.
              </p>
              
              <div style={styles.errorDetails}>
                <h3 style={styles.detailsTitle}>Error Details</h3>
                <pre style={styles.errorText}>
                  {this.state.error && this.state.error.toString()}
                </pre>
              </div>

              <div style={styles.actions}>
                <button 
                  style={styles.primaryButton}
                  onClick={() => window.location.reload()}
                >
                  Reload Application
                </button>
                <button 
                  style={styles.secondaryButton}
                  onClick={() => window.location.href = '/'}
                >
                  Go to Homepage
                </button>
              </div>

              <div style={styles.techInfo}>
                <p>Environment: {window.CONFIG?.environment || 'unknown'}</p>
                <p>Time: {new Date().toISOString()}</p>
              </div>
            </div>
          </div>
        );
      }

      // Generic error - show general message
      return (
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <div style={styles.errorIcon}>🚨</div>
            <h1 style={styles.title}>Something went wrong</h1>
            <p style={styles.message}>
              An unexpected error occurred. Please try refreshing the page.
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <div style={styles.errorDetails}>
                <h3 style={styles.detailsTitle}>Error Stack (Development Only)</h3>
                <pre style={styles.errorText}>
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            <div style={styles.actions}>
              <button 
                style={styles.primaryButton}
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              <button 
                style={styles.secondaryButton}
                onClick={() => this.setState({ hasError: false })}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px'
  },
  errorCard: {
    background: 'white',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '600px',
    width: '100%',
    padding: '40px'
  },
  errorIcon: {
    fontSize: '60px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  title: {
    color: '#2c3e50',
    textAlign: 'center',
    marginBottom: '10px',
    fontSize: '28px',
    fontWeight: '600'
  },
  message: {
    color: '#7f8c8d',
    textAlign: 'center',
    marginBottom: '30px',
    fontSize: '16px',
    lineHeight: '1.6'
  },
  errorDetails: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '30px'
  },
  detailsTitle: {
    color: '#495057',
    fontSize: '14px',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  errorText: {
    color: '#dc3545',
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflow: 'auto',
    maxHeight: '200px'
  },
  actions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  primaryButton: {
    padding: '12px 30px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#667eea',
    color: 'white',
    transition: 'all 0.3s ease'
  },
  secondaryButton: {
    padding: '12px 30px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    background: '#e9ecef',
    color: '#495057',
    transition: 'all 0.3s ease'
  },
  techInfo: {
    textAlign: 'center',
    color: '#adb5bd',
    fontSize: '12px',
    borderTop: '1px solid #e9ecef',
    paddingTop: '20px'
  }
};

export default ConfigErrorBoundary;